import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { useObservableState } from 'observable-hooks';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import ShowIfAllowed from '../../../../components/common/show-if-allowed';

import Table from '../../../../components/common/table/table';
import Title from '../../../../components/common/title';
import Form from '../../../../components/form';
import Checkbox from '../../../../components/form/elements/checkbox';
import Group from '../../../../components/form/elements/group';
import Link, { LinkComponent } from '../../../../components/form/elements/link';
import Readonly from '../../../../components/form/elements/readonly';
import Show from '../../../../components/form/elements/show';
import Text from '../../../../components/form/elements/text';
import TextArea from '../../../../components/form/elements/text-area';
import Partner from '../../../../interfaces/partner';
import Department from '../../../../interfaces/partnerDepartment';
import {
  HUMAN_PARTNER_DEPARTMENTS_CREATE,
  HUMAN_PARTNER_DEPARTMENTS_EDIT,
  HUMAN_PARTNERS_EDIT,
  HUMAN_PARTNERS_INDEX,
} from '../../../../router/routes';
import { useService } from '../../../../services/services';
import { can, route } from '../../../../services/utils';
import { RootState } from '../../../../store/store';
import css from './view-partner.module.css';

const schema = yup.object().shape({
  last_name: yup.string().max(255).required(),
  first_name: yup.string().max(255).required(),
  position: yup.string().max(255).required(),
});

export default function ViewPartner() {
  const { t } = useTranslation();
  const { partnerService, partnerDepartmentService } = useService();
  const [body, setBody] = useState([]);
  const [footer, setFooter] = useState([]);
  const [departmentsTable, setDepartmentsTable] = useState(<></>);
  const { farmId, id } = useParams<{ farmId: string; id: string }>();

  const { farmUser } = useSelector((state: RootState) => ({
    farmUser: state.global.farmUser,
  }));

  const [partner] = useObservableState<Partner>(
    () => partnerService.show(farmId, id),
    null
  );

  const departmentsRequest = useCallback(
    (page, sort, desc) => partnerDepartmentService.index(farmId, id),
    [partnerDepartmentService, farmId, id]
  );

  useEffect(() => {
    if (partner === null) {
      return;
    }
    setBody([
      Readonly([
        Group([
          Text(
            'form_of_economic_activity_value',
            'classifiers.form_of_economic_activity'
          ),
          Text('name'),
          Text('registration_number'),
          Checkbox('vat_payer'),
          Show(
            Text('vat_number'),
            'vat_payer',
            (vatPayer: boolean) => vatPayer
          ),
          Text('contact_person'),
          Text('contact_phone'),
          Text('email'),
          Text('bank'),
          Text('bank_account_number'),
          Checkbox('active'),
          Text('legal_address'),
          Text('actual_address'),
          TextArea('comment'),
        ]),
      ]),
    ]);

    if (partner && !partner.not_editable) {
      setFooter(() => {
        let elements = [];

        if (can(farmUser, { title: 'update partner' })) {
          elements.push(
            Link(
              route(HUMAN_PARTNERS_EDIT, { farmId, id: partner.id }),
              'edit',
              true
            )
          );
        }
        return elements;
      });
    }

    if (
      partner &&
      !partner.not_editable &&
      can(farmUser, { title: 'viewAny partner_department' })
    ) {
      setDepartmentsTable(
        <div className={css['table']}>
          <div className={css['toolbar']}>
            <div className={css['left']}>
              <div>
                <Title
                  title={t('pages.human.partners.departments')}
                  uppercase={false}
                />
              </div>
            </div>
            <ShowIfAllowed ability={{ title: 'create partner_department' }}>
              <div className={css['right']}>
                <LinkComponent
                  label="add"
                  primary
                  icon={faPlus}
                  linkTo={route(HUMAN_PARTNER_DEPARTMENTS_CREATE, {
                    farmId,
                    partnerId: id,
                  })}
                />
              </div>
            </ShowIfAllowed>
          </div>
          <Table<Department>
            request={departmentsRequest}
            keyAccessor="id"
            viewRoute={route(HUMAN_PARTNER_DEPARTMENTS_EDIT, {
              farmId,
              partnerId: id,
            })}
            viewRecordAbility={{ title: 'update partner_department' }}
            columns={[
              { accessor: 'name', header: t('name') },
              { accessor: 'address', header: t('address') },
              { accessor: 'comment', header: t('comment') },
            ]}
            paginate={false}
          />
        </div>
      );
    }
  }, [
    farmUser,
    partner,
    setBody,
    setFooter,
    farmId,
    departmentsRequest,
    id,
    t,
  ]);

  return (
    <div className={css['view']}>
      <Form
        title={
          partner &&
          partner.form_of_economic_activity_value?.concat(' - ', partner.name)
        }
        backTo={route(HUMAN_PARTNERS_INDEX, { farmId })}
        mode="view"
        className={css['form']}
        noticeAccessor="ViewForm"
        body={body}
        footer={footer}
        values={partner}
        schema={schema}
      />
      {departmentsTable}
    </div>
  );
}
